// Pick the best src from an img's srcset.
// Prefers the candidate closest to (and at least) targetWidth.
// Falls back to currentSrc then src if srcset is absent or unparseable.
function resolveBestSrc(imageEl, targetWidth = 1200) {
  const srcset = imageEl.srcset;
  if (!srcset) return imageEl.currentSrc || imageEl.src;

  const candidates = srcset.split(",").map(s => {
    const parts = s.trim().split(/\s+/);
    const url   = parts[0];
    const w     = parts[1] ? parseInt(parts[1], 10) : 0;
    return { url, w };
  }).filter(c => c.url);

  if (!candidates.length) return imageEl.currentSrc || imageEl.src;

  candidates.sort((a, b) => a.w - b.w);

  const match = candidates.find(c => c.w >= targetWidth);
  if (match) return match.url;

  return candidates[candidates.length - 1].url;
}

// ------- desktop fullscreen modal

function openDesktopModal(clickedImageEl, allImages) {
  // Backdrop
  const modal = document.createElement("div");
  modal.className = "zoom-modal";
  modal.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: #fff;
    overflow-y: auto;
    overflow-x: hidden;
  `;

  // Close button — fixed so it stays visible while scrolling
  const closeBtn = document.createElement("button");
  closeBtn.className = "zoom-modal__close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 2L18 18M18 2L2 18" stroke="#111" stroke-width="2" stroke-linecap="round"/>
  </svg>`;
  closeBtn.style.cssText = `
    position: fixed;
    top: 16px;
    right: 20px;
    z-index: 10001;
    background: rgba(0,0,0,0.06);
    border: 1px solid rgba(0,0,0,0.12);
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
  `;

  // Vertical stack of all images at full width
  const stack = document.createElement("div");
  stack.className = "zoom-modal__stack";
  stack.style.cssText = `
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 60px 120px 40px;
    gap: 16px;
    box-sizing: border-box;
  `;

  let clickedWrapper = null;

  allImages.forEach(imgEl => {
    const wrapper = document.createElement("div");
    wrapper.style.cssText = `
      width: 100%;
      max-width: 100vw;
      line-height: 0;
    `;

    const img = document.createElement("img");
    img.src = resolveBestSrc(imgEl);
    img.alt = imgEl.alt || "";
    img.style.cssText = `
      width: 100%;
      height: auto;
      display: block;
      object-fit: contain;
    `;

    wrapper.appendChild(img);
    stack.appendChild(wrapper);

    if (imgEl === clickedImageEl) {
      clickedWrapper = wrapper;
    }
  });

  modal.appendChild(stack);
  modal.appendChild(closeBtn);
  document.body.appendChild(modal);

  // Prevent body scroll while modal is open
  document.body.style.overflow = "hidden";

  // Loader — covers the modal until we're ready to scroll
  const loader = document.createElement("div");
  loader.className = "zoom-modal__loader";
  loader.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  loader.innerHTML = `<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg"
    style="animation: zoom-spin 0.8s linear infinite;">
    <circle cx="18" cy="18" r="14" stroke="rgba(0,0,0,0.1)" stroke-width="3"/>
    <path d="M18 4a14 14 0 0 1 14 14" stroke="#111" stroke-width="3" stroke-linecap="round"/>
    <style>@keyframes zoom-spin { to { transform: rotate(360deg); } }</style>
  </svg>`;
  modal.appendChild(loader);

  if (clickedWrapper) {
    const allWrappers  = Array.from(stack.children);
    const clickedIndex = allWrappers.indexOf(clickedWrapper);
    const imagesAbove  = allWrappers
      .slice(0, clickedIndex)
      .map(w => w.querySelector("img"))
      .filter(Boolean);

    const loadPromises = imagesAbove.map(img =>
      img.complete
        ? Promise.resolve()
        : new Promise(res => { img.onload = res; img.onerror = res; })
    );

    Promise.all(loadPromises).then(() => {
      clickedWrapper.scrollIntoView({ block: "start" });
      loader.remove();
    });
  } else {
    loader.remove();
  }

  function close() {
    modal.remove();
    document.body.style.overflow = "";
    window.removeEventListener("keydown", onKeydown);
  }

  function onKeydown(e) {
    if (e.key === "Escape") close();
  }

  closeBtn.addEventListener("click", close);
  window.addEventListener("keydown", onKeydown);

  modal._close = close;
  return modal;
}

// ------- mobile pinch zoom (< 768px)
// Pinch → scale, two-finger drag → translate, double-tap → toggle zoom.
function enablePinchZoom(imageEl) {
  const el     = imageEl;
  const parent = el.parentElement;

  let scale      = 1;
  let translateX = 0;
  let translateY = 0;
  let lastTapTime = 0;

  let startDist  = 0;
  let startScale = 1;
  let startMidX  = 0;
  let startMidY  = 0;
  let startTX    = 0;
  let startTY    = 0;

  function clampTranslate(tx, ty, s) {
    const parentW   = parent.offsetWidth;
    const parentH   = parent.offsetHeight;
    const overflowX = (parentW * s - parentW) / 2;
    const overflowY = (parentH * s - parentH) / 2;
    return {
      tx: Math.min(Math.max(tx, -overflowX), overflowX),
      ty: Math.min(Math.max(ty, -overflowY), overflowY),
    };
  }

  function applyTransform(s, tx, ty, animated = false) {
    el.style.transition = animated ? "transform 0.25s ease" : "none";
    el.style.transform  = `translate(${tx}px, ${ty}px) scale(${s})`;
  }

  function getTouchDist(t) {
    return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  }

  function getTouchMid(t) {
    return {
      x: (t[0].clientX + t[1].clientX) / 2,
      y: (t[0].clientY + t[1].clientY) / 2,
    };
  }

  el.addEventListener("touchstart", (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      startDist  = getTouchDist(e.touches);
      startScale = scale;
      const mid  = getTouchMid(e.touches);
      startMidX  = mid.x;
      startMidY  = mid.y;
      startTX    = translateX;
      startTY    = translateY;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapTime < 300) {
        e.preventDefault();
        if (scale > 1) {
          // Already zoomed — reset to normal
          scale = 1; translateX = 0; translateY = 0;
          applyTransform(1, 0, 0, true);
        } else {
          // Zoom in 2× centered on tap point
          const touch = e.touches[0] || e.changedTouches[0];
          const rect  = el.getBoundingClientRect();
          const tapX  = touch.clientX - rect.left - rect.width  / 2;
          const tapY  = touch.clientY - rect.top  - rect.height / 2;
          scale       = 2;
          const { tx, ty } = clampTranslate(-tapX, -tapY, scale);
          translateX = tx; translateY = ty;
          applyTransform(scale, translateX, translateY, true);
        }
      }
      lastTapTime = now;
    }
  }, { passive: false });

  el.addEventListener("touchmove", (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist     = getTouchDist(e.touches);
      const newScale = Math.min(Math.max(startScale * (dist / startDist), 1), 4);
      const mid      = getTouchMid(e.touches);
      const rawTX    = startTX + (mid.x - startMidX);
      const rawTY    = startTY + (mid.y - startMidY);
      const { tx, ty } = clampTranslate(rawTX, rawTY, newScale);
      scale = newScale; translateX = tx; translateY = ty;
      applyTransform(scale, translateX, translateY);
    }
  }, { passive: false });

  el.addEventListener("touchend", () => {
    if (scale < 1) {
      scale = 1; translateX = 0; translateY = 0;
      applyTransform(1, 0, 0, true);
    }
  });

  el.style.transformOrigin = "center center";
  el.style.willChange      = "transform";
  if (getComputedStyle(parent).overflow === "visible") {
    parent.style.overflow = "hidden";
  }

  function reset() {
    if (scale === 1 && translateX === 0 && translateY === 0) return;
    scale = 1; translateX = 0; translateY = 0;
    applyTransform(1, 0, 0, true);
  }

  el._pinchReset = reset;
}

// ------- global state
window.zoomState = window.zoomState || { currentModal: null };

// ------- init
function enableZoom({ container = "[data-magnify='true']", imageSelector = "img" } = {}) {
  const containerEl =
    typeof container === "string" ? document.querySelector(container) : container;
  if (!containerEl) throw new Error("Zoom container not found");

  const isMobile = () => window.innerWidth < 768;

  // Mobile: attach pinch zoom to every image
  containerEl.querySelectorAll(imageSelector).forEach(enablePinchZoom);

  // Mobile: reset all images when a slide class changes (e.g. Flickity carousel)
  const slideObserver = new MutationObserver((mutations) => {
    if (!isMobile()) return;
    const classChanged = mutations.some(
      m => m.type === "attributes" && m.attributeName === "class"
    );
    if (!classChanged) return;
    containerEl.querySelectorAll(imageSelector).forEach(img => {
      if (typeof img._pinchReset === "function") img._pinchReset();
    });
  });

  slideObserver.observe(containerEl, {
    attributes: true,
    attributeFilter: ["class"],
    subtree: true,
  });

  // Desktop: click any image → fullscreen modal with all images stacked
  containerEl.addEventListener("click", (evt) => {
    if (isMobile()) return;

    const imageEl = evt.target.closest(imageSelector);
    if (!imageEl) return;

    evt.preventDefault();
    evt.stopPropagation();

    // Close any open modal first
    if (window.zoomState.currentModal) {
      window.zoomState.currentModal._close();
      window.zoomState.currentModal = null;
    }

    const allImages = Array.from(containerEl.querySelectorAll(imageSelector));
    const modal     = openDesktopModal(imageEl, allImages);

    // Wrap _close so global state is cleared when modal closes from any trigger
    const originalClose = modal._close;
    modal._close = () => {
      originalClose();
      window.zoomState.currentModal = null;
    };

    window.zoomState.currentModal = modal;
  });
}

document.addEventListener("DOMContentLoaded", () => {
  enableZoom({ container: "[data-magnify='true']", imageSelector: "img" });
});
